import { Fragment, useMemo } from 'react';

import PortalEntry from '@/components/common/PortalEntry';
import { FlattenPortalRequest, RequestWorkflowResponse } from '@/types/sso';
import { addDashesToCharacter } from '@/utils/format';
import useLanguage, { UseLanguageReturn } from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';

interface BankDetailsProps {
    selectedBankAccount?: FlattenPortalRequest;
    workflowsData?: RequestWorkflowResponse[];
}

const BankDetails = {
    Section1: (props: BankDetailsProps) => {
        const { selectedBankAccount, workflowsData } = props;
        const lang = useLanguage();
        const { instrumentCurrenciesApi } = useSso();
        const { data: instrumentCurrenciesData = [] } = instrumentCurrenciesApi;

        const currencyName = useMemo(() => {
            if (selectedBankAccount?.baseCurrencyName) return selectedBankAccount.baseCurrencyName;
            if (instrumentCurrenciesData) {
                const currency = instrumentCurrenciesData.find((c) => c.id === selectedBankAccount?.baseCurrencyId);
                return currency ? currency.assetName || currency.code : selectedBankAccount?.baseCurrencyId;
            }
        }, [instrumentCurrenciesData, selectedBankAccount]);

        const additionalAccountNumber = useMemo(() => {
            if (currencyName === 'GBP') {
                return addDashesToCharacter(selectedBankAccount?.additionalAccountNumber || '');
            }
            return selectedBankAccount?.additionalAccountNumber;
        }, [currencyName, selectedBankAccount]);

        return (
            <Fragment>
                <fieldset className="space-y-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.generalInformation}</legend>
                    <PortalEntry value={selectedBankAccount?.friendlyName} label={lang.portal.friendlyName} />
                    <PortalEntry value={currencyName} label={lang.lite.currency} />
                </fieldset>
                <fieldset className="space-y-3 mt-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.bankAccountInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.bankName} label={lang.portal.bankName} />
                        </div>
                        <div className="flex-1">
                            <PortalEntry
                                value={selectedBankAccount?.beneficiaryName}
                                label={lang.portal.beneficiaryName}
                            />
                        </div>
                    </div>
                </fieldset>
                <fieldset className="space-y-3">
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.bankAddress} label={lang.portal.bankAddress} />
                        </div>
                    </div>
                </fieldset>
                <fieldset className="space-y-3">
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        {selectedBankAccount?.country && (
                            <PortalEntry value={selectedBankAccount?.country} label={lang.portal.country} />
                        )}
                        <PortalEntry value={selectedBankAccount?.swiftCode} label="SWIFT/BIC" />
                    </div>
                </fieldset>
                <fieldset className="space-y-3">
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.ibanCode} label="IBAN" />
                        </div>
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.accountNumber} label={lang.portal.accountNumber} />
                        </div>
                    </div>
                </fieldset>
                <fieldset>
                    {selectedBankAccount?.additionalAccountNumber && (
                        <PortalEntry
                            label={getAdditionalAccountNumberLabel(selectedBankAccount?.baseCurrencyName, lang)}
                            value={additionalAccountNumber || '-'}
                        />
                    )}
                </fieldset>
                <fieldset>
                    <PortalEntry value={selectedBankAccount?.description} label={lang.portal.requestDescription} />
                </fieldset>
                {workflowsData && workflowsData.length > 1 && workflowsData[1]?.remark && (
                    <PortalEntry value={workflowsData[1].remark} label={lang.portal.remark} />
                )}
            </Fragment>
        );
    },
    Section2: (props: BankDetailsProps) => {
        const { selectedBankAccount } = props;
        const lang = useLanguage();
        return (
            <Fragment>
                <fieldset className="space-y-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.correspondentInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry
                                value={selectedBankAccount?.correspondentBankName}
                                label={lang.portal.bankName}
                            />
                        </div>
                        <div className="flex-1">
                            <PortalEntry
                                value={selectedBankAccount?.correspondentBankSwiftCode}
                                label={lang.portal.swiftCode}
                            />
                        </div>
                    </div>
                </fieldset>

                <fieldset className="space-y-3 mt-3 w-full">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.intermediaryInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry
                                value={selectedBankAccount?.intermediaryBankName}
                                label={lang.portal.bankName}
                            />
                        </div>
                        <div className="flex-1">
                            <PortalEntry
                                value={selectedBankAccount?.intermediaryBankSwiftCode}
                                label={lang.portal.swiftCode}
                            />
                        </div>
                    </div>
                </fieldset>
            </Fragment>
        );
    },
    Summary: (props: BankDetailsProps) => {
        const { selectedBankAccount } = props;

        const lang = useLanguage();

        const swiftCodeLabel = useMemo(() => {
            return selectedBankAccount?.currencyName === 'USD'
                ? lang.portal.swiftBICABARoutingNumber
                : lang.portal.swiftBIC;
        }, [lang, selectedBankAccount]);

        return (
            <Fragment>
                <fieldset className="space-y-3 mt-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.bankAccountInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.bankName} label={lang.portal.bankName} />
                        </div>
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.bankAddress} label={lang.portal.bankAddress} />
                        </div>
                    </div>
                </fieldset>
                <fieldset>
                    <PortalEntry value={selectedBankAccount?.swiftCode} label={swiftCodeLabel} />
                </fieldset>
                <fieldset className="space-y-3">
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.ibanCode} label="IBAN" />
                        </div>
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.accountNumber} label={lang.portal.accountNumber} />
                        </div>
                    </div>
                </fieldset>
            </Fragment>
        );
    }
};

export default BankDetails;

export const getAdditionalAccountNumberLabel = (currencyLabel: string, lang: UseLanguageReturn) => {
    switch (currencyLabel) {
        case 'USD':
            return lang.portal.abaRoutingNumber;
        case 'GBP':
            return lang.portal.sortCode;
        case 'AUD':
            return lang.portal.bsbNumber;
        case 'DKK':
            return lang.portal.registrationNumber;
        default:
            return lang.portal.additionalAccountNumber;
    }
};
